import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";

export function useFilterParam(
  paramKey: string,
  defaultValue: string = "all",
  aliases: string[] = []
) {
  const [searchParams, setSearchParams] = useSearchParams();

  const getParamVal = useCallback(() => {
    const val = searchParams.get(paramKey);
    if (val !== null && val !== "") return val;
    for (const alias of aliases) {
      const aliasVal = searchParams.get(alias);
      if (aliasVal !== null && aliasVal !== "") return aliasVal;
    }
    return defaultValue;
  }, [searchParams, paramKey, defaultValue, aliases]);

  const [filter, setFilter] = useState<string>(getParamVal);

  useEffect(() => {
    const currentVal = getParamVal();
    if (currentVal !== filter) {
      setFilter(currentVal);
    }
  }, [getParamVal, filter]);

  const updateFilter = useCallback(
    (newVal: string) => {
      setFilter(newVal);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (newVal && newVal !== defaultValue) {
            next.set(paramKey, newVal);
          } else {
            next.delete(paramKey);
          }
          for (const alias of aliases) {
            next.delete(alias);
          }
          return next;
        },
        { replace: true }
      );
    },
    [paramKey, defaultValue, aliases, setSearchParams]
  );

  return [filter, updateFilter] as const;
}
